import moment from "moment";
import config from "lib/config";
import format from "lib/format";

const MOBILE_VIEWPORT_THRESHOLD = "750px";
const DATE_FORMAT =
  config && config.shortDateFormat && config.shortDateFormat.toUpperCase();

// Adjust color for Accessibility contrast
export function getCorrectTextColor(hex) {
  const threshold = 130;

  const hRed = hexToR(hex);
  const hGreen = hexToG(hex);
  const hBlue = hexToB(hex);

  function hexToR(h) {
    return parseInt(cutHex(h).substring(0, 2), 16);
  }
  function hexToG(h) {
    return parseInt(cutHex(h).substring(2, 4), 16);
  }
  function hexToB(h) {
    return parseInt(cutHex(h).substring(4, 6), 16);
  }
  function cutHex(h) {
    return h.charAt(0) == "#" ? h.substring(1, 7) : h;
  }

  const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
  if (cBrightness > threshold) {
    return "#000000";
  }
  return "#ffffff";
}

export const mobileViewport =
  window.matchMedia(`(max-width: ${MOBILE_VIEWPORT_THRESHOLD})`).matches ||
  false;

export const getViewportSize = () => {
  if (window.matchMedia("(max-width: 750px)").matches) {
    return "mobile";
  }
  if (window.matchMedia("(max-width: 960px)").matches) {
    return "small";
  }
  if (window.matchMedia("(max-width: 1080px)").matches) {
    return "mediumSmall";
  }
  if (window.matchMedia("(max-width: 1280px)").matches) {
    return "medium";
  }
  return "large";
};

export function removeDuplicateObjects(arr) {
  let unique_array = [];
  for (let i = 0; i < arr.length; i++) {
    if (
      !unique_array.find(
        (uniqueItem) => JSON.stringify(uniqueItem) === JSON.stringify(arr[i]),
      )
    ) {
      unique_array.push(arr[i]);
    }
  }
  return unique_array;
}

export function getItemsWithUniqueID(array) {
  const uniqueArray = [];
  array.forEach((element) => {
    if (
      uniqueArray.findIndex(
        (uniqueElement) => uniqueElement.id == element.id,
      ) === -1
    ) {
      uniqueArray.push(element);
    }
  });
  return uniqueArray;
}

export function removeDuplicates(arr) {
  let unique_array = [];
  for (let i = 0; i < arr.length; i++) {
    if (unique_array.indexOf(arr[i]) == -1) {
      unique_array.push(arr[i]);
    }
  }
  return unique_array;
}

export function getCurrentYears(forwards, backwards) {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - backwards; i <= forwards + currentYear; i++) {
    years.push(i);
  }
  return years;
}

// sort according to the attribute
export function compareAttributes(a, b, attribute, direction) {
  const aValue =
    typeof a[attribute] === "string"
      ? a[attribute].toUpperCase()
      : a[attribute];
  const bValue =
    typeof b[attribute] === "string"
      ? b[attribute].toUpperCase()
      : b[attribute];

  if (aValue === null) {
    return direction ? 1 : -1;
  } else if (bValue === null) {
    return direction ? -1 : 1;
  }

  if (aValue > bValue) {
    return direction ? 1 : -1;
  } else if (aValue < bValue || bValue === null) {
    return direction ? -1 : 1;
  }
  return 0;
}

export function filterItems(items, filter, keyId) {
  let updatedItems = items.filter((item) => {
    return item[keyId].toLowerCase().search(filter.toLowerCase()) !== -1;
  });
  return updatedItems;
}

export const getPersonalAvatarSrc = (personalId, size = "small") => {
  return format.url(
    `${location.origin}${config.urlRoot}Personal/StreamImage.aspx?pres=2&pid=${personalId}&size=${size}`,
  );
};

/**
 * Extracts and returns the initials from a given full name.
 *
 * This function processes a string representing a person's full name and returns the initials.
 * - For names with more than two words, it returns the initials of the first and last words.
 * - For names with one or two words, it returns the initials of each word.
 * - The initials are returned in uppercase.
 *
 * @param {string} name - The full name from which to extract initials. It can be a single name or multiple words.
 * @returns {string} The uppercase initials derived from the given name.
 *
 * @example
 * getNameInitials("John Doe"); // Returns "JD"
 * getNameInitials("John Michael Doe"); // Returns "JD"
 * getNameInitials("Alice"); // Returns "A"
 * getNameInitials(""); // Returns ""
 */
export const getNameInitials = (name) => {
  if (!name || !name.trim()) {
    return "";
  }

  let words = name.split(" ");
  let initials = "";

  if (words.length > 2) {
    initials = words[0].charAt(0) + words[words.length - 1].charAt(0);
  } else {
    for (let word of words) {
      initials += word.charAt(0);
    }
  }

  return initials.toUpperCase();
};

export function getDateTimeString(date, format = `${DATE_FORMAT} HH:mm`) {
  const formattedDate = moment.utc(date).format(format);
  return formattedDate;
}

export function getDateString(date) {
  const d = moment.utc(date).tz(config.timeZone).format(DATE_FORMAT);

  return moment(date).isSame(moment(), "day")
    ? moment.utc(date).tz(config.timeZone).format("HH:mm")
    : d;
}

export function getCourseDateFormat(date) {
  const isValidCourseTime = !(
    new Date(date).getHours() === 0 && new Date(date).getMinutes() === 0
  );

  return isValidCourseTime ? `${DATE_FORMAT} HH:mm` : DATE_FORMAT;
}

/**
 * Gets the current date formatted according to the specified timezone and date format.
 *
 * This function uses the current date and time, converts it to the configured timezone,
 * and formats it according to the predefined date format.
 *
 * @returns {string} The formatted current date string.
 */
export function getCurrentDateInTimeZone() {
  const date = new Date();

  let d = moment.utc(date).tz(config.timeZone).format(DATE_FORMAT);

  return d;
}

export function upperCaseFirstLetter(params) {
  if (typeof params === "string") {
    return params.charAt(0).toUpperCase() + params.slice(1);
  }
  return null;
}

export function checkAndAddLastCharacter(str, char) {
  if (str.substring(str.length - 1) === char) {
    return str;
  }

  return str + char;
}

export function convertSecondsToHHMMSS(secs) {
  try {
    if (isNaN(secs) || secs < 0) {
      return "";
    }

    let sec_num = parseInt(secs, 10);
    let hours = Math.floor(sec_num / 3600);
    let minutes = Math.floor((sec_num - hours * 3600) / 60);
    var seconds = sec_num - hours * 3600 - minutes * 60;

    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }

    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return hours + ":" + minutes + ":" + seconds;
  } catch (error) {
    return "";
  }
}

export function getLangCode(id) {
  switch (id) {
    case "1":
      return "en";
      break;
    case "2":
      return "sv";
      break;
    case "4":
      return "nb";
      break;
    case "5":
      return "es";
      break;
    case "6":
      return "de";
      break;
    case "7":
      return "da";
      break;
    case "8":
      return "fr";
      break;
    case "9":
      return "fi";
      break;
    case "11":
      return "pt";
      break;
    default:
      return "sv";
  }
}

export const Helpers = {
  // Main wrapper for Fetch API
  httpRequest: (url, method, payload, headers) => {
    // Configuration to accept json as a default
    const config = {
      method,
      headers: {
        "Content-Type": "application/json",
      },
    };

    // method = post and payload, add it to the fetch request
    if (method.toLowerCase() === "post" && payload && payload.length > 0) {
      config.body = JSON.stringify(payload);
    }
    // if custom headers need to be set for the specific request
    // override them here
    if (
      headers &&
      typeof headers === "object" &&
      Object.keys(headers).length > 0
    ) {
      config.headers = headers;
    }
    return fetch(url, config).then((response) => {
      // Check if the request is 200
      if (response.ok) {
        let data = response;

        // if the type is json return, interpret it as json
        if (
          response.headers.get("Content-Type").indexOf("application/json") > -1
        ) {
          data = response.json();
        }
        return data;
      }
      // if an errors, anything but 200 then reject with the actuall response
      return Promise.reject(response);
    });
  },
};

export const checkExternalURL = (url) => {
  try {
    url = new URL(url);
    return url.href;
  } catch (_) {
    if (url.indexOf("http://") === 0 || url.indexOf("https://") === 0) {
      return url;
    }
    return "http://" + url;
  }
};

export const openExternalURL = (url) => {
  window.open(checkExternalURL(url), "_blank").focus();
};

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

/**
 * Generates a random key string of the specified length.
 *
 * @param {number} length - The length of the key string to generate.
 * @returns {string} The randomly generated key string.
 */
export function generateRandomKeyIdString(length = 32) {
  const characters =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}[]|\\:;<>?,./";
  let key = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    key += characters.charAt(randomIndex);
  }
  return key;
}

export const AddLinebreaksRemoveHtmlTags = (text) => {
  return text.replace(/<(?:br|\/div|\/p)>/g, "\n").replace(/<.*?>/g, "");
};

export const ReplaceLineBreaksWithHtmlTags = (text) => {
  if (text) {
    if (!ContainsHtmlElement(text))
      return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
  }
  return text;
};

export const ContainsHtmlElement = (text) => {
  return /<\/?[a-z][\s\S]*>/i.test(text);
};

export const getCssVariableOrFallback = (cssVar, fallbackValue) => {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar)
      .trim() || fallbackValue
  );
};

/**
 * Sorts an array of objects based on a specified property and order.
 *
 * @param {Object[]} items - The array of objects to be sorted.
 * @param {string} sortBy - The property name to sort the items by.
 * @param {string} currentSortBy - The property name that is currently being used for sorting.
 * @param {string} currentSortOrder - The current order ('asc' or 'desc') of the sorting.
 *
 * @returns {Object} An object containing the sorted items array and the new sorting criteria.
 * @returns {Object[]} .sortedItems - The sorted array of items.
 * @returns {string} .sortBy - The property name used for sorting in this operation.
 * @returns {string} .sortOrder - The order ('asc' or 'desc') used for sorting in this operation.
 */
export function sortItems(items, sortBy, currentSortBy, currentSortOrder) {
  let sortOrder = "desc";

  if (currentSortBy === sortBy) {
    sortOrder = currentSortOrder === "asc" ? "desc" : "asc";
  } else {
    sortOrder = "asc";
  }

  const sortedItems = items.sort((a, b) => {
    if (sortOrder === "asc") {
      return a[sortBy] > b[sortBy] ? 1 : a[sortBy] < b[sortBy] ? -1 : 0;
    } else {
      return a[sortBy] < b[sortBy] ? 1 : a[sortBy] > b[sortBy] ? -1 : 0;
    }
  });

  return { sortedItems, sortBy, sortOrder };
}

const ONLINE_COURSE_TYPE = "onlinecourse";
export function isStandardActivityType(type) {
  const standardTypes = [
    "programcommonactivity",
    "programotheractivity",
    "program",
    "internalcourse",
    ONLINE_COURSE_TYPE,
    "instructorledtraining",
    "commonactivity",
    "externalcourse",
    "otheractivity",
    "completedimportedcourse",
  ];

  const normalized = typeof type === "string" ? type.toLowerCase() : type;
  return standardTypes.includes(normalized);
}

export function isOnlineCourse(activity, externalLMSCourseType) {
  return (
    activity.type === ONLINE_COURSE_TYPE ||
    activity.type === externalLMSCourseType
  );
}

export function escapeHtml(text) {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
    "/": "&#x2F;",
  };

  return text.replace(/[&<>"'/]/g, (m) => map[m]);
}

export function insertHtml(text) {
  const map = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&#x2F;": "/",
  };

  return text.replace(/&amp;|&lt;|&gt;|&quot;|&#39;|&#x2F;/g, (m) => map[m]);
}

/**
 * Checks if a string contains at least a specified number of characters matching a given regular expression.
 *
 * @param {string} string - The string to check.
 * @param {RegExp} regex - The regular expression to match characters.
 * @param {number} [minCount=1] - The minimum number of matching characters required. Default is 1.
 * @returns {boolean} - Returns true if the string contains the minimum number of matching characters, otherwise false.
 */
function hasRequiredCharacters(string, regex, minCount = 1) {
  if (minCount === 0) {
    return string.length > 0 && !/^\s+$/.test(string);
  }
  const matches = string.match(regex) || [];
  return matches.length >= minCount;
}

/**
 * Checks if a string contains at least a specified number of uppercase letters.
 *
 * @param {string} string - The string to check.
 * @param {number} [minCount=1] - The minimum number of uppercase letters required. Default is 1.
 * @returns {boolean} - Returns true if the string contains the minimum number of uppercase letters, otherwise false.
 */
export function hasUpperCase(string, minCount = 1) {
  return hasRequiredCharacters(string, /[A-Z]/g, minCount);
}

/**
 * Checks if a string contains at least a specified number of lowercase letters.
 *
 * @param {string} string - The string to check.
 * @param {number} [minCount=1] - The minimum number of lowercase letters required. Default is 1.
 * @returns {boolean} - Returns true if the string contains the minimum number of lowercase letters, otherwise false.
 */
export function hasLowerCase(string, minCount = 1) {
  return hasRequiredCharacters(string, /[a-z]/g, minCount);
}

/**
 * Checks if a string contains at least a specified number of digits.
 *
 * @param {string} string - The string to check.
 * @param {number} [minCount=1] - The minimum number of digits required. Defaults to 1.
 * @returns {boolean} - Returns true if the string contains the minimum number of digits, otherwise false.
 */
export function hasNumber(string, minCount = 1) {
  return hasRequiredCharacters(string, /\d/g, minCount);
}

/**
 * Checks if a string contains at least a specified number of special characters.
 *
 * @param {string} string - The string to check.
 * @param {number} [minCount=1] - The minimum number of special characters required.
 * @returns {boolean} - Returns true if the string contains the minimum number of special characters, otherwise false.
 */
export function hasSpecialCharacter(string, minCount = 1) {
  return hasRequiredCharacters(string, /[!@#$%^&*(),.?":{}|<>]/g, minCount);
}

/**
 * Creates a full URL by appending the given path to the base URL of the current window location.
 *
 * @param {string} path - The path to append to the base URL.
 * @returns {string} - The full URL.
 */
export const createPortalPathUrl = (path) => {
  const baseUrl = window.location.href.split("#")[0];
  return `${baseUrl}${path}`;
};
